// Initialize Telegram Analytics for tapps store listing
// Configure via VITE_TELEGRAM_ANALYTICS_TOKEN and VITE_TELEGRAM_ANALYTICS_APP_NAME env vars 
import telegramAnalytics from '@telegram-apps/analytics';

// Configuration constants
// Note: Adsgram Block ID is public and expected to be in client code
// It's not a secret - it's an identifier for ad placement similar to Google AdSense IDs
const ADSGRAM_BLOCK_ID = import.meta.env.VITE_ADSGRAM_BLOCK_ID || "20075";

// Credit and payment constants (aligned with backend)
const CREDIT_PACK_AMOUNT = 10; // Credits per purchase
const CREDIT_PACK_COST = 5;    // Stars cost
const POLL_INTERVAL_MS = 3000; // Poll every 3s
const POLL_TIMEOUT_MS = 30000; // 30s timeout
const SYNCING_OPACITY = '0.7'; // Opacity during sync

// Only initialize analytics if both token and appName are configured
const analyticsToken = import.meta.env.VITE_TELEGRAM_ANALYTICS_TOKEN;
const analyticsAppName = import.meta.env.VITE_TELEGRAM_ANALYTICS_APP_NAME;

if (analyticsToken && analyticsAppName) {
    try {
        telegramAnalytics.init({
            token: analyticsToken,
            appName: analyticsAppName,
        });
    } catch (error) {
        console.warn('Failed to initialize Telegram Analytics:', error);
    }
}

// Initialize Telegram WebApp
function initTelegramWebApp() {
    const tg = window.Telegram.WebApp;
    tg.expand();
    tg.ready();
    return tg;
}

// Wait for Telegram SDK to be ready
let tg;
if (window.Telegram && window.Telegram.WebApp) {
    tg = initTelegramWebApp();
} else {
    // Wait for mock to load
    document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => {
            if (window.Telegram && window.Telegram.WebApp) {
                tg = initTelegramWebApp();
                initApp();
            }
        }, 100);
    });
}

// State management
const state = {
    credits: 1,
    lastCalculation: null,
    adsgramLoaded: false,
    isCalculating: false,
    isWatchingAd: false,
    isPro: false,
    hasWatchedAd: false,
    hasSpentCredit: false,
    // Polling state for credit synchronization
    isSyncing: false,
    pendingCredits: null,
    pollInterval: null,
    pollStartTime: null
};

// DOM elements
const elements = {
    // Form inputs
    loanType: document.getElementById('loan-type'),
    loanAmount: document.getElementById('loan-amount'),
    interestRate: document.getElementById('interest-rate'),
    loanTerm: document.getElementById('loan-term'),
    termUnit: document.getElementById('term-unit'),
    originationFee: document.getElementById('origination-fee'),
    extraPayment: document.getElementById('extra-payment'),
    
    // Buttons
    calculateBtn: document.getElementById('calculate-btn'),
    copyBtn: document.getElementById('copy-btn'),
    resetBtn: document.getElementById('reset-btn'),
    watchAdBtn: document.getElementById('watch-ad-btn'),
    buyCreditsBtn: document.getElementById('buy-credits-btn'),
    buyProBtn: document.getElementById('buy-pro-btn'),
    advancedToggle: document.getElementById('advanced-toggle'),
    showScheduleBtn: document.getElementById('show-schedule-btn'),
    
    // Sections
    advancedOptions: document.getElementById('advanced-options'),
    resultsSection: document.getElementById('results-section'),
    scheduleSection: document.getElementById('schedule-section'),
    earnCreditsSection: document.querySelector('.earn-credits-section'),
    
    // Results
    monthlyPayment: document.getElementById('monthly-payment'),
    totalPayment: document.getElementById('total-payment'),
    totalInterest: document.getElementById('total-interest'),
    payoffDate: document.getElementById('payoff-date'),
    scheduleBody: document.getElementById('schedule-body'),
    
    // Other
    creditsCount: document.getElementById('credits-count'),
    proStatus: document.getElementById('pro-status'),
    calculateCreditCost: document.getElementById('calculate-credit-cost'),
    loadingOverlay: document.getElementById('loading-overlay'),
    loadingText: document.getElementById('loading-text')
};

// Get user ID from Telegram
function getUserId() {
    // Note: For production, you should validate the Telegram init data signature
    // to ensure authenticity and prevent tampering. See:
    // https://core.telegram.org/bots/webapps#validating-data-received-via-the-mini-app
    return tg?.initDataUnsafe?.user?.id || null;
}

// Sync credits with server
async function syncCredits() {
    const userId = getUserId();
    if (!userId) {
        console.warn('No user ID available, using localStorage fallback');
        const savedCredits = localStorage.getItem('loancalc_credits');
        state.credits = savedCredits ? parseInt(savedCredits, 10) : 1;
        state.isPro = false;
        updateCreditsDisplay();
        return;
    }

    try {
        const response = await fetch(`/api/get-user-status?userId=${userId}`);
        const data = await response.json();
        
        if (data.success) {
            state.credits = data.credits;
            state.isPro = data.isPro;
            updateCreditsDisplay();
            console.log(`User status synced from server:`, data);
        } else {
            throw new Error(data.message || 'Failed to sync user status');
        }
    } catch (error) {
        console.error('Error syncing user status:', error);
        // Fallback to localStorage if server fails
        const savedCredits = localStorage.getItem('loancalc_credits');
        state.credits = savedCredits ? parseInt(savedCredits, 10) : 1;
        state.isPro = false;
        updateCreditsDisplay();
    }
}

// Poll credits once (single poll against /api/get-user-status)
async function pollCreditsOnce() {
    const userId = getUserId();
    if (!userId) {
        console.warn('No user ID available for polling');
        return false;
    }

    try {
        const response = await fetch(`/api/get-user-status?userId=${userId}`);
        const data = await response.json();
        
        if (data.success) {
            const oldCredits = state.credits;
            const oldIsPro = state.isPro;
            
            state.credits = data.credits;
            state.isPro = data.isPro;
            
            // Check if expected state was reached
            const expectedCredits = state.pendingCredits;
            let stateChanged = false;
            
            if (expectedCredits === 'pro') {
                // Waiting for Pro status
                stateChanged = state.isPro && !oldIsPro;
            } else if (typeof expectedCredits === 'number') {
                // Waiting for specific credit amount
                stateChanged = state.credits >= expectedCredits;
            } else {
                // Waiting for any change
                stateChanged = state.credits !== oldCredits || state.isPro !== oldIsPro;
            }
            
            updateCreditsDisplay();
            
            if (stateChanged) {
                console.log(`✓ Poll detected expected change: credits ${oldCredits} → ${state.credits}, pro ${oldIsPro} → ${state.isPro}`);
                return true;
            } else {
                console.log(`Poll in progress: credits ${state.credits}, pro ${state.isPro}, waiting for: ${expectedCredits}`);
                return false;
            }
        } else {
            console.warn('Poll failed:', data.message);
            return false;
        }
    } catch (error) {
        console.error('Error polling credits:', error);
        return false;
    }
}

// Stop credit polling
function stopCreditPolling() {
    if (state.pollInterval) {
        clearInterval(state.pollInterval);
        state.pollInterval = null;
    }
    state.isSyncing = false;
    state.pendingCredits = null;
    state.pollStartTime = null;
    updateCreditsDisplay();
    console.log('Credit polling stopped');
}

// Start credit polling with expected state
// expectedCredits: number for credit amount, 'pro' for Pro status, null for any change
function startCreditPolling(expectedCredits = null) {
    // Clear any existing polling
    stopCreditPolling();
    
    // Set polling state
    state.isSyncing = true;
    state.pendingCredits = expectedCredits;
    state.pollStartTime = Date.now();
    updateCreditsDisplay();
    
    console.log(`Starting credit polling (expecting: ${expectedCredits})`);
    
    // Start polling interval
    state.pollInterval = setInterval(async () => {
        // Check for timeout
        if (Date.now() - state.pollStartTime > POLL_TIMEOUT_MS) {
            console.warn('Credit polling timeout reached');
            stopCreditPolling();
            return;
        }
        
        // Poll once
        const success = await pollCreditsOnce();
        
        // Stop polling if successful
        if (success) {
            stopCreditPolling();
        }
    }, POLL_INTERVAL_MS);
    
    // Also poll immediately
    pollCreditsOnce().then(success => {
        if (success) {
            stopCreditPolling();
        }
    });
}

// Start credit polling with expected credit increase
function startCreditPollingWithExpected(expectedAmount, reason = '') {
    const expectedTotal = state.credits + expectedAmount;
    console.log(`Starting polling for ${expectedAmount} credits (${state.credits} → ${expectedTotal}) ${reason}`);
    startCreditPolling(expectedTotal);
}

// Initialize credits from server
async function initCredits() {
    await syncCredits();
}

// Update credits display
function updateCreditsDisplay() {
    if (state.isPro) {
        elements.creditsCount.textContent = '∞';
        if (elements.proStatus) {
            elements.proStatus.style.display = 'inline';
        }
        // Hide credit cost on calculate button for Pro users
        if (elements.calculateCreditCost) {
            elements.calculateCreditCost.style.display = 'none';
        }
        // Hide entire "Earn Credits" section for Pro users
        if (elements.earnCreditsSection) {
            elements.earnCreditsSection.style.display = 'none';
        }
        // Hide watch ad button for Pro users
        if (elements.watchAdBtn) {
            elements.watchAdBtn.style.display = 'none';
        }
        // Hide buy credits button for Pro users
        if (elements.buyCreditsBtn) {
            elements.buyCreditsBtn.style.display = 'none';
        }
        // Hide buy pro button (already Pro)
        if (elements.buyProBtn) {
            elements.buyProBtn.style.display = 'none';
        }
    } else {
        // Show syncing indicator if polling
        if (state.isSyncing && state.pendingCredits !== null && typeof state.pendingCredits === 'number') {
            elements.creditsCount.textContent = `${state.credits} → ${state.pendingCredits}...`;
            elements.creditsCount.style.opacity = SYNCING_OPACITY;
        } else if (state.isSyncing) {
            elements.creditsCount.textContent = `${state.credits}...`;
            elements.creditsCount.style.opacity = SYNCING_OPACITY;
        } else {
            elements.creditsCount.textContent = state.credits;
            elements.creditsCount.style.opacity = '1';
        }
        
        if (elements.proStatus) {
            elements.proStatus.style.display = 'none';
        }
        // Show credit cost on calculate button for non-Pro users
        if (elements.calculateCreditCost) {
            elements.calculateCreditCost.style.display = 'inline';
        }
        // Show "Earn Credits" section for non-Pro users
        if (elements.earnCreditsSection) {
            elements.earnCreditsSection.style.display = 'block';
        }
        // Show buttons for non-Pro users
        if (elements.watchAdBtn) {
            elements.watchAdBtn.style.display = 'inline-block';
        }
        if (elements.buyCreditsBtn) {
            elements.buyCreditsBtn.style.display = 'inline-block';
        }
        if (elements.buyProBtn) {
            elements.buyProBtn.style.display = 'inline-block';
        }
    }
    updateCalculateButtonState();
}

// Update calculate button state
function updateCalculateButtonState() {
    const hasRequiredInputs = isFormValid();
    // Pro users always have access, non-Pro users need credits
    elements.calculateBtn.disabled = !hasRequiredInputs || (!state.isPro && state.credits < 1);
}

// Check if form has valid inputs
function isFormValid() {
    const amount = parseFloat(elements.loanAmount.value);
    const rate = parseFloat(elements.interestRate.value);
    const term = parseFloat(elements.loanTerm.value);
    
    return amount > 0 && rate >= 0 && rate <= 100 && term > 0;
}

// Save credits to server
async function saveCredits() {
    const userId = getUserId();
    if (!userId) {
        console.warn('No user ID available, saving to localStorage');
        localStorage.setItem('loancalc_credits', state.credits.toString());
        updateCreditsDisplay();
        return;
    }

    try {
        const response = await fetch('/api/update-credits', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                userId,
                amount: state.credits,
                operation: 'set'
            })
        });

        const data = await response.json();
        
        if (data.success) {
            console.log(`Credits saved to server: ${data.credits}`);
            updateCreditsDisplay();
        } else {
            throw new Error(data.message || 'Failed to save credits');
        }
    } catch (error) {
        console.error('Error saving credits:', error);
        // Fallback to localStorage if server fails
        localStorage.setItem('loancalc_credits', state.credits.toString());
        updateCreditsDisplay();
    }
}

// Add credits (for legacy/fallback only - prefer server-side operations)
async function addCredits(amount) {
    const userId = getUserId();
    if (!userId) {
        console.warn('No user ID available, using localStorage');
        state.credits += amount;
        localStorage.setItem('loancalc_credits', state.credits.toString());
        updateCreditsDisplay();
        if (tg) tg.showAlert(`You earned ${amount} credit${amount > 1 ? 's' : ''}!`);
        return;
    }

    try {
        const response = await fetch('/api/update-credits', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                userId,
                amount: amount,
                operation: 'add'
            })
        });

        const data = await response.json();
        
        if (data.success) {
            state.credits = data.credits;
            updateCreditsDisplay();
            if (tg) tg.showAlert(`You earned ${amount} credit${amount > 1 ? 's' : ''}!`);
        } else {
            throw new Error(data.message || 'Failed to add credits');
        }
    } catch (error) {
        console.error('Error adding credits:', error);
        // Fallback to localStorage if server fails
        state.credits += amount;
        localStorage.setItem('loancalc_credits', state.credits.toString());
        updateCreditsDisplay();
        if (tg) tg.showAlert(`You earned ${amount} credit${amount > 1 ? 's' : ''}!`);
    }
}

// Deduct credits
async function deductCredits(amount) {
    const userId = getUserId();
    if (!userId) {
        console.warn('No user ID available, using localStorage');
        if (state.credits >= amount) {
            state.credits -= amount;
            localStorage.setItem('loancalc_credits', state.credits.toString());
            updateCreditsDisplay();
            return true;
        }
        return false;
    }

    try {
        const response = await fetch('/api/update-credits', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                userId,
                amount: amount,
                operation: 'subtract'
            })
        });

        const data = await response.json();
        
        if (data.success) {
            state.credits = data.credits;
            updateCreditsDisplay();
            console.log(`Credits deducted: ${data.credits}`);
            return true;
        } else {
            console.error('Failed to deduct credits:', data.message);
            return false;
        }
    } catch (error) {
        console.error('Error deducting credits:', error);
        // Fallback to localStorage if server fails
        if (state.credits >= amount) {
            state.credits -= amount;
            localStorage.setItem('loancalc_credits', state.credits.toString());
            updateCreditsDisplay();
            return true;
        }
        return false;
    }
}

// Load Adsgram SDK
function loadAdsgramSDK() {
    if (state.adsgramLoaded) return Promise.resolve();
    
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://sad.adsgram.ai/js/sad.min.js';
        script.onload = () => {
            state.adsgramLoaded = true;
            resolve();
        };
        script.onerror = () => reject(new Error('Failed to load Adsgram SDK'));
        document.head.appendChild(script);
    });
}

// Calculate loan
// Constants for floating point precision handling
const BALANCE_PRECISION_THRESHOLD = 0.01;

function calculateLoan(principal, annualRate, termMonths, extraMonthly = 0) {
    const monthlyRate = annualRate / 100 / 12;
    
    // Handle zero interest rate (use small tolerance for floating point comparison)
    if (Math.abs(monthlyRate) < Number.EPSILON) {
        const monthlyPayment = principal / termMonths;
        const schedule = [];
        let balance = principal;
        
        for (let month = 1; month <= Math.min(termMonths, 12); month++) {
            const payment = Math.min(monthlyPayment + extraMonthly, balance);
            balance = Math.max(0, balance - payment);
            
            schedule.push({
                month,
                payment: payment,
                principal: payment,
                interest: 0,
                balance: balance
            });
            
            if (balance <= BALANCE_PRECISION_THRESHOLD) break;
        }
        
        return {
            monthlyPayment: monthlyPayment,
            totalPayment: principal,
            totalInterest: 0,
            actualTermMonths: termMonths,
            schedule
        };
    }
    
    // Standard loan calculation using EMI formula
    const monthlyPayment = principal * 
        (monthlyRate * Math.pow(1 + monthlyRate, termMonths)) / 
        (Math.pow(1 + monthlyRate, termMonths) - 1);
    
    // Generate amortization schedule
    const schedule = [];
    let balance = principal;
    let totalPaid = 0;
    let actualMonths = 0;
    
    for (let month = 1; month <= termMonths && balance > 0; month++) {
        const interestPayment = balance * monthlyRate;
        let principalPayment = monthlyPayment - interestPayment + extraMonthly;
        
        // Handle final payment
        if (principalPayment > balance) {
            principalPayment = balance;
        }
        
        const totalMonthlyPayment = principalPayment + interestPayment;
        balance = Math.max(0, balance - principalPayment);
        totalPaid += totalMonthlyPayment;
        actualMonths = month;
        
        // Only store first 12 months for display
        if (month <= 12) {
            schedule.push({
                month,
                payment: totalMonthlyPayment,
                principal: principalPayment,
                interest: interestPayment,
                balance: balance
            });
        }
        
        // Account for floating point precision
        if (balance <= BALANCE_PRECISION_THRESHOLD) break;
    }
    
    // Calculate totals for remaining months if more than 12
    if (actualMonths > 12) {
        let tempBalance = schedule[11]?.balance || 0;
        for (let month = 13; month <= actualMonths && tempBalance > 0; month++) {
            const interestPayment = tempBalance * monthlyRate;
            let principalPayment = monthlyPayment - interestPayment + extraMonthly;
            if (principalPayment > tempBalance) {
                principalPayment = tempBalance;
            }
            totalPaid += principalPayment + interestPayment;
            tempBalance = Math.max(0, tempBalance - principalPayment);
        }
    }
    
    const totalInterest = totalPaid - principal;
    
    return {
        monthlyPayment,
        totalPayment: totalPaid,
        totalInterest,
        actualTermMonths: actualMonths,
        schedule
    };
}

// Format currency
function formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
}

// Format date
function formatPayoffDate(months) {
    const date = new Date();
    date.setMonth(date.getMonth() + months);
    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short'
    }).format(date);
}

// Display results
function displayResults(results) {
    elements.monthlyPayment.textContent = formatCurrency(results.monthlyPayment);
    elements.totalPayment.textContent = formatCurrency(results.totalPayment);
    elements.totalInterest.textContent = formatCurrency(results.totalInterest);
    elements.payoffDate.textContent = formatPayoffDate(results.actualTermMonths);
    
    // Clear and populate schedule
    elements.scheduleBody.innerHTML = '';
    results.schedule.forEach(row => {
        const tr = document.createElement('tr');
        tr.innerHTML = `
            <td>${row.month}</td>
            <td>${formatCurrency(row.payment)}</td>
            <td>${formatCurrency(row.principal)}</td>
            <td>${formatCurrency(row.interest)}</td>
            <td>${formatCurrency(row.balance)}</td>
        `;
        elements.scheduleBody.appendChild(tr);
    });
    
    elements.resultsSection.style.display = 'block';
    state.lastCalculation = results;
}

// Generate copy text
function generateCopyText() {
    if (!state.lastCalculation) return '';
    
    const loanType = elements.loanType.options[elements.loanType.selectedIndex].text;
    const amount = parseFloat(elements.loanAmount.value);
    const rate = parseFloat(elements.interestRate.value);
    const term = parseFloat(elements.loanTerm.value);
    const termUnit = elements.termUnit.value;
    
    let text = `Loan Calculation Summary\n`;
    text += `------------------------\n`;
    text += `Type: ${loanType}\n`;
    text += `Amount: ${formatCurrency(amount)}\n`;
    text += `Interest Rate: ${rate}%\n`;
    text += `Term: ${term} ${termUnit}\n`;
    text += `\n`;
    text += `Monthly Payment: ${formatCurrency(state.lastCalculation.monthlyPayment)}\n`;
    text += `Total Payment: ${formatCurrency(state.lastCalculation.totalPayment)}\n`;
    text += `Total Interest: ${formatCurrency(state.lastCalculation.totalInterest)}\n`;
    text += `Payoff Date: ${formatPayoffDate(state.lastCalculation.actualTermMonths)}`;
    
    return text;
}

// Show Adsgram ad
async function showAdsgramAd() {
    // Prevent multiple clicks
    if (state.isWatchingAd) {
        console.log('Ad already in progress, ignoring click');
        return;
    }
    
    // Check if user is Pro
    if (state.isPro) {
        if (tg) tg.showAlert('You have Pro status with unlimited credits. No need to watch ads!');
        return;
    }
    
    // Set loading state
    state.isWatchingAd = true;
    elements.watchAdBtn.disabled = true;
    elements.watchAdBtn.classList.add('loading');
    const originalText = elements.watchAdBtn.innerHTML;
    elements.watchAdBtn.innerHTML = 'Checking...';
    
    const resetButtonState = () => {
        state.isWatchingAd = false;
        elements.watchAdBtn.disabled = false;
        elements.watchAdBtn.classList.remove('loading');
        elements.watchAdBtn.innerHTML = originalText;
    };
    
    try {
        const userId = getUserId();
        if (!userId) {
            resetButtonState();
            if (tg) tg.showAlert('Unable to verify user. Please try again.');
            return;
        }
        
        // Check if user can watch ad (anti-fraud)
        const checkResponse = await fetch(`/api/can-watch-ad?userId=${userId}`);
        const checkData = await checkResponse.json();
        
        if (!checkData.success || !checkData.allowed) {
            resetButtonState();
            if (tg) tg.showAlert(checkData.reason || 'You cannot watch ads at this time.');
            return;
        }
        
        await loadAdsgramSDK();
        
        // Initialize Adsgram with configured block ID
        const AdController = window.Adsgram.init({ blockId: ADSGRAM_BLOCK_ID });
        
        elements.watchAdBtn.innerHTML = 'Watching Ad...';
        
        AdController.show().then(async () => {
            // User watched the ad successfully
            // Start polling for expected credit increase (10 credits)
            const AD_REWARD = 10;
            startCreditPollingWithExpected(AD_REWARD, 'after ad reward');
            
            // Call server to grant reward
            try {
                const recordResponse = await fetch('/api/record-ad-watched', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ userId })
                });
                
                const recordData = await recordResponse.json();
                
                if (recordData.success) {
                    // Stop polling and use authoritative response
                    stopCreditPolling();
                    state.credits = recordData.credits;
                    state.hasWatchedAd = true;
                    updateCreditsDisplay();
                    const creditsEarned = recordData.amount || AD_REWARD;
                    if (tg) tg.showAlert(recordData.message || `You earned ${creditsEarned} credits!`);
                } else {
                    // API failed, keep polling as fallback
                    console.error('Failed to record ad:', recordData.message);
                    if (tg) tg.showAlert('Processing ad reward...');
                }
            } catch (error) {
                console.error('Error recording ad:', error);
                // Keep polling as fallback if API call fails
                if (tg) tg.showAlert('Processing ad reward...');
            }
            resetButtonState();
        }).catch((result) => {
            resetButtonState();
            // User closed the ad or an error occurred
            if (result.error) {
                if (tg) tg.showAlert('Failed to load ad. Please try again later.');
            } else {
                if (tg) tg.showAlert('Ad was closed before completion.');
            }
        });
    } catch (error) {
        console.error('Adsgram error:', error);
        resetButtonState();
        if (tg) tg.showAlert('Ad system error. Please try again later.');
    }
}

// Initialize app
async function initApp() {
    await initCredits();
    setupEventListeners();
    applyTheme();
}

// Setup event listeners
function setupEventListeners() {
    // Form input listeners for validation
    const formInputs = [
        elements.loanAmount,
        elements.interestRate,
        elements.loanTerm
    ];
    
    formInputs.forEach(input => {
        input.addEventListener('input', updateCalculateButtonState);
    });
    
    // Advanced options toggle
    elements.advancedToggle.addEventListener('click', () => {
        const isVisible = elements.advancedOptions.style.display !== 'none';
        elements.advancedOptions.style.display = isVisible ? 'none' : 'block';
        elements.advancedToggle.classList.toggle('active', !isVisible);
    });
    
    // Calculate button
    elements.calculateBtn.addEventListener('click', handleCalculate);
    
    // Show schedule toggle
    elements.showScheduleBtn.addEventListener('click', () => {
        const isVisible = elements.scheduleSection.style.display !== 'none';
        elements.scheduleSection.style.display = isVisible ? 'none' : 'block';
        elements.showScheduleBtn.textContent = isVisible ? 
            'View Amortization Schedule' : 'Hide Amortization Schedule';
    });
    
    // Copy button
    elements.copyBtn.addEventListener('click', handleCopy);
    
    // Reset button
    elements.resetBtn.addEventListener('click', handleReset);
    
    // Watch ad button
    elements.watchAdBtn.addEventListener('click', showAdsgramAd);
    
    // Buy credits button
    elements.buyCreditsBtn.addEventListener('click', handlePurchaseCredits);
    
    // Buy Pro button
    if (elements.buyProBtn) {
        elements.buyProBtn.addEventListener('click', handlePurchasePro);
    }
}

// Apply Telegram theme
function applyTheme() {
    if (tg && tg.themeParams) {
        document.documentElement.style.setProperty('--tg-theme-bg-color', tg.themeParams.bg_color || '#f8f9fa');
        document.documentElement.style.setProperty('--tg-theme-text-color', tg.themeParams.text_color || '#212529');
        document.documentElement.style.setProperty('--tg-theme-hint-color', tg.themeParams.hint_color || '#6c757d');
        document.documentElement.style.setProperty('--tg-theme-button-color', tg.themeParams.button_color || '#0d6efd');
        document.documentElement.style.setProperty('--tg-theme-button-text-color', tg.themeParams.button_text_color || '#ffffff');
        document.documentElement.style.setProperty('--tg-theme-secondary-bg-color', tg.themeParams.secondary_bg_color || '#ffffff');
    }

    // Log ready state
    console.log('Telegram WebApp initialized:', {
        version: tg ? tg.version : 'mock',
        platform: tg ? tg.platform : 'web',
        colorScheme: tg ? tg.colorScheme : 'light'
    });
}

// Handle calculate
async function handleCalculate() {
    // Prevent multiple calculations
    if (state.isCalculating) {
        console.log('Already calculating, ignoring click');
        return;
    }
    
    if (!isFormValid()) {
        if (tg) tg.showAlert('Please fill in all required fields with valid values.');
        return;
    }
    
    // Pro users don't need credits
    if (!state.isPro && state.credits < 1) {
        if (tg) tg.showAlert('Not enough credits. Watch an ad or purchase credits.');
        return;
    }
    
    // Set calculating state
    state.isCalculating = true;
    elements.calculateBtn.disabled = true;
    elements.calculateBtn.classList.add('loading');
    
    // Deduct credit (only for non-Pro users)
    let deducted = true;
    if (!state.isPro) {
        deducted = await deductCredits(1);
        if (!deducted) {
            state.isCalculating = false;
            elements.calculateBtn.disabled = false;
            elements.calculateBtn.classList.remove('loading');
            if (tg) tg.showAlert('Not enough credits!');
            return;
        }
    }
    
    // Show loading
    elements.loadingOverlay.style.display = 'flex';
    
    try {
        // Get form values
        const amount = parseFloat(elements.loanAmount.value);
        const rate = parseFloat(elements.interestRate.value);
        let termMonths = parseFloat(elements.loanTerm.value);
        const originationFee = parseFloat(elements.originationFee.value) || 0;
        const extraPayment = parseFloat(elements.extraPayment.value) || 0;
        
        // Convert years to months if needed
        if (elements.termUnit.value === 'years') {
            termMonths = termMonths * 12;
        }
        
        // Include origination fee in principal
        const totalPrincipal = amount + originationFee;
        
        // Simulate brief calculation delay for UX
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Calculate
        const results = calculateLoan(totalPrincipal, rate, termMonths, extraPayment);
        
        // Display results
        displayResults(results);
        
        // Hide loading
        elements.loadingOverlay.style.display = 'none';
        state.isCalculating = false;
        elements.calculateBtn.classList.remove('loading');
        updateCalculateButtonState();
        
        // Scroll to results
        elements.resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        
    } catch (error) {
        console.error('Error calculating loan:', error);
        
        elements.loadingOverlay.style.display = 'none';
        state.isCalculating = false;
        elements.calculateBtn.classList.remove('loading');
        updateCalculateButtonState();
        
        if (tg) tg.showAlert('Calculation failed. Your credit has been refunded.');
        
        // Refund credit on error (only for non-Pro users)
        if (!state.isPro && deducted) {
            await addCredits(1);
        }
    }
}

// Handle copy
async function handleCopy() {
    const text = generateCopyText();
    if (!text) {
        if (tg) tg.showAlert('No results to copy.');
        return;
    }
    
    try {
        await navigator.clipboard.writeText(text);
        
        // Show success state
        const originalText = elements.copyBtn.textContent;
        elements.copyBtn.textContent = 'Copied';
        elements.copyBtn.classList.add('copied');
        
        setTimeout(() => {
            elements.copyBtn.textContent = originalText;
            elements.copyBtn.classList.remove('copied');
        }, 2000);
        
    } catch (error) {
        console.error('Failed to copy:', error);
        if (tg) tg.showAlert('Failed to copy to clipboard.');
    }
}

// Handle reset
function handleReset() {
    // Reset form
    elements.loanAmount.value = '';
    elements.interestRate.value = '';
    elements.loanTerm.value = '';
    elements.originationFee.value = '';
    elements.extraPayment.value = '';
    elements.loanType.selectedIndex = 0;
    elements.termUnit.selectedIndex = 0;
    
    // Reset state
    state.lastCalculation = null;
    state.isCalculating = false;
    
    // Hide sections
    elements.resultsSection.style.display = 'none';
    elements.scheduleSection.style.display = 'none';
    elements.advancedOptions.style.display = 'none';
    elements.advancedToggle.classList.remove('active');
    elements.showScheduleBtn.textContent = 'View Amortization Schedule';
    
    // Update button state
    updateCalculateButtonState();
}

// Handle purchase credits
async function handlePurchaseCredits() {
    if (!tg || !tg.openInvoice) {
        if (tg) tg.showAlert('Payment feature is not available in this environment.');
        return;
    }

    // Show loading state
    elements.buyCreditsBtn.disabled = true;
    elements.buyCreditsBtn.innerHTML = 'Creating invoice...';

    try {
        // Get user ID from Telegram WebApp
        const userId = tg.initDataUnsafe?.user?.id || null;

        // Call serverless function to create invoice
        const response = await fetch('/api/create-invoice', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ userId })
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
            throw new Error(data.message || 'Failed to create invoice');
        }

        // Open Telegram invoice
        tg.openInvoice(data.invoiceLink, async (status) => {
            if (status === 'paid') {
                // Start polling for expected credit increase
                startCreditPollingWithExpected(CREDIT_PACK_AMOUNT, 'after payment');
                
                // Attempt immediate sync (may succeed if webhook was fast)
                await syncCredits();
                
                // Polling will automatically stop when expected credits are reached
                // Show success message - credits may still be syncing
                if (tg) tg.showAlert('Payment successful! Credits are being added to your account.');
            } else if (status === 'cancelled') {
                if (tg) tg.showAlert('Payment was cancelled.');
            } else if (status === 'failed') {
                if (tg) tg.showAlert('Payment failed. Please try again.');
            }
        });
    } catch (error) {
        console.error('Error creating invoice:', error);
        if (tg) tg.showAlert(`Failed to create payment: ${error.message}`);
    } finally {
        // Reset button state
        elements.buyCreditsBtn.disabled = false;
        elements.buyCreditsBtn.innerHTML = 'Buy 10 Credits <span class="credit-cost">(5 Stars)</span>';
    }
}

// Handle purchase unlimited credits
async function handlePurchasePro() {
    if (!tg || !tg.openInvoice) {
        if (tg) tg.showAlert('Payment feature is not available in this environment.');
        return;
    }
    
    // Check if already Pro
    if (state.isPro) {
        if (tg) tg.showAlert('You already have unlimited credits!');
        return;
    }

    // Show loading state
    elements.buyProBtn.disabled = true;
    const originalText = elements.buyProBtn.innerHTML;
    elements.buyProBtn.innerHTML = 'Creating invoice...';

    try {
        // Get user ID from Telegram WebApp
        const userId = tg.initDataUnsafe?.user?.id || null;

        // Call serverless function to create unlimited credits invoice
        const response = await fetch('/api/create-pro-invoice', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ userId })
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
            throw new Error(data.message || 'Failed to create invoice');
        }

        // Open Telegram invoice
        tg.openInvoice(data.invoiceLink, async (status) => {
            if (status === 'paid') {
                // Start polling for Pro status
                startCreditPolling('pro');
                
                // Attempt immediate sync (may succeed if webhook was fast)
                await syncCredits();
                
                // Check if Pro status activated
                if (state.isPro) {
                    // Pro already activated, stop polling
                    stopCreditPolling();
                    if (tg) tg.showAlert('Payment successful! You now have unlimited credits forever!');
                }
                // Otherwise, polling continues until Pro status confirms or timeout
            } else if (status === 'cancelled') {
                if (tg) tg.showAlert('Payment was cancelled.');
            } else if (status === 'failed') {
                if (tg) tg.showAlert('Payment failed. Please try again.');
            }
        });
    } catch (error) {
        console.error('Error creating invoice:', error);
        if (tg) tg.showAlert(`Failed to create payment: ${error.message}`);
    } finally {
        // Reset button state
        elements.buyProBtn.disabled = false;
        elements.buyProBtn.innerHTML = originalText;
    }
}

// Start app if Telegram is already loaded
if (tg) {
    initApp();
}
